import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  CreateNutritionPlanInput,
  ListNutritionPlansInput,
  NutritionPlanRepository,
  UpdateNutritionPlanInput,
} from '../../application/ports/nutrition-plan.repository';
import type {NutritionPlan} from '../../domain/nutrition-plan';
import type {NutritionPlanId} from '../../domain/nutrition-plan-id.value-object';
import type {NutritionPlanError} from '../../domain/errors';
import {
  InvalidPlanInputError,
  NetworkError,
  PlanNotFoundError,
  PlanOverlapError,
  ServerError,
  UnauthorizedError,
} from '../../domain/errors';
import type {NutritionPlanDtoToNutritionPlanMapper} from '../mappers/nutrition-plan-dto-to-nutrition-plan.mapper';

export class RpcNutritionPlanRepository implements NutritionPlanRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly planMapper: NutritionPlanDtoToNutritionPlanMapper,
  ) {}

  async list(
    input: ListNutritionPlansInput,
  ): Promise<Result<Page<NutritionPlan>, NutritionPlanError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['nutrition-plans'].$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.q.length > 0 && {q: input.q}),
          },
        }),
      );

      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.planMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async create(
    input: CreateNutritionPlanInput,
  ): Promise<Result<NutritionPlan, NutritionPlanError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['nutrition-plans'].$post({
          json: {
            name: input.name,
            targets: input.targets,
            startDate: input.startDate,
            endDate: input.endDate,
          },
        }),
      );
      return ok(this.planMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async update(
    input: UpdateNutritionPlanInput,
  ): Promise<Result<NutritionPlan, NutritionPlanError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['nutrition-plans'][':nutritionPlanId'].$patch({
          param: {nutritionPlanId: input.planId},
          json: {
            ...(input.name !== undefined && {name: input.name}),
            ...(input.targets !== undefined && {targets: input.targets}),
            ...(input.startDate !== undefined && {startDate: input.startDate}),
            ...(input.endDate !== undefined && {endDate: input.endDate}),
          },
        }),
      );
      return ok(this.planMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async delete(planId: NutritionPlanId): Promise<Result<void, NutritionPlanError>> {
    try {
      await parseResponse(
        this.rpcClient['nutrition-plans'][':nutritionPlanId'].$delete({
          param: {nutritionPlanId: planId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  private mapError(cause: unknown): Result<never, NutritionPlanError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new PlanNotFoundError());
      }
      if (cause.statusCode === 409) {
        return err(new PlanOverlapError());
      }
      if (cause.statusCode === 400) {
        return err(new InvalidPlanInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
